import React, { useState, useEffect } from "react";
import axios from "axios";
import Offcanvas from "react-bootstrap/Offcanvas";

const AccountUploadForm = ({ show, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        year: "",
        total_revenue: "",
        vat_payable: "",
        vat_collected: "",
    });

    const [expenses, setExpenses] = useState([
        { parentGroup: "", parentAmount: "", ledgerExpenses: [] }
    ]);

    const [bankAccounts, setBankAccounts] = useState([{ bank: "", amount: "" }]);
    const [groups, setGroups] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [banks, setBanks] = useState([]);

    const API_BASE_URL = process.env.REACT_APP_WEBZASH_URL;
    const API_KEY = process.env.REACT_APP_API_KEY;

    useEffect(() => {
        if (show) {
            fetchGroupsAndLedgers();
            fetchBanks();
        }
    }, [show]);

    const fetchGroupsAndLedgers = async () => {
        try {
            const headers = {
                "x-api-key": API_KEY,
                Accept: "application/json",
            };

            const [ledgersRes, groupsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/ledgers/index`, { headers }),
                axios.get(`${API_BASE_URL}/api/groups/index`, { headers }),
            ]);

            setLedgers(ledgersRes.data?.data || []);
            setGroups(groupsRes.data || []);
        } catch (err) {
            console.error("Error loading data", err);
        }
    };

    const fetchBanks = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/bank-accounts`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setBanks(response.data.account || []);
        } catch (error) {
            console.error('Error fetching banks:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const getParentExpenseGroups = () => {
        return groups.filter(g =>
            ["Direct Expenses", "Indirect Expenses", "Purchases", "Expenses"]
                .includes(g.Group?.name)
        );
    };

    const getLedgersForParentGroup = (parentGroupName) => {
        const parentGroup = groups.find(g =>
            g.Group?.name?.toLowerCase() === parentGroupName?.toLowerCase()
        );

        if (!parentGroup) return [];

        const parentGroupId = parentGroup.Group.id;
        const childGroups = groups.filter(g => g.Group?.parent_id === parentGroupId);
        const allGroupIds = [parentGroupId, ...childGroups.map(g => g.Group.id)];

        return ledgers.filter(l => allGroupIds.includes(l.Ledger?.group_id));
    };

    const addExpenseRow = () => {
        setExpenses([...expenses, { parentGroup: "", parentAmount: "", ledgerExpenses: [] }]);
    };

    const updateParentGroup = (index, parentGroup) => {
        const updated = [...expenses];
        updated[index].parentGroup = parentGroup;
        updated[index].parentAmount = "";

        const ledgersForGroup = getLedgersForParentGroup(parentGroup);
        updated[index].ledgerExpenses = ledgersForGroup.map(ledger => ({
            ledgerId: ledger.Ledger.id,
            ledgerName: ledger.Ledger.name,
            amount: ""
        }));

        setExpenses(updated);
    };

    const updateParentAmount = (index, amount) => {
        const updated = [...expenses];
        updated[index].parentAmount = amount;
        setExpenses(updated);
    };

    const updateLedgerAmount = (expenseIndex, ledgerId, amount) => {
        const updated = [...expenses];
        const ledgerExpenses = updated[expenseIndex].ledgerExpenses;
        const updatedLedgerExpenses = ledgerExpenses.map(exp =>
            exp.ledgerId === ledgerId ? { ...exp, amount } : exp
        );
        updated[expenseIndex].ledgerExpenses = updatedLedgerExpenses;
        setExpenses(updated);
    };

    const removeExpenseRow = (index) => {
        setExpenses(expenses.filter((_, i) => i !== index));
    };

    const addBankRow = () => {
        setBankAccounts([...bankAccounts, { bank: "", amount: "" }]);
    };

    const updateBankAccount = (i, field, value) => {
        const updated = [...bankAccounts];
        updated[i][field] = value;
        setBankAccounts(updated);
    };

    const ensureLedgerExists = async (ledgerName, groupName, openingBalance, balanceType) => {
        const headers = {
            "x-api-key": API_KEY,
            Accept: "application/json",
            "Content-Type": "application/json"
        };

        let ledger = ledgers.find(l =>
            l.Ledger?.name?.trim().toLowerCase() === ledgerName?.trim().toLowerCase()
        )?.Ledger;

        if (ledger) return ledger.id;

        let group = groups.find(g =>
            g.Group?.name?.trim().toLowerCase() === groupName?.trim().toLowerCase()
        )?.Group;

        if (!group) {
            if (groupName.includes("Direct") || balanceType === "D") {
                group = groups.find(g => g.Group?.name?.toLowerCase() === "direct expenses")?.Group;
            } else if (groupName.includes("Indirect")) {
                group = groups.find(g => g.Group?.name?.toLowerCase() === "indirect expenses")?.Group;
            } else if (groupName.includes("Purchase")) {
                group = groups.find(g => g.Group?.name?.toLowerCase() === "purchases")?.Group;
            }

            if (!group) {
                group = groups.find(g => g.Group?.name?.toLowerCase() === "expenses")?.Group;
            }
        }

        if (!group) {
            throw new Error(`Group "${groupName}" not found and no suitable fallback group available`);
        }

        const payload = {
            Ledger: {
                name: ledgerName,
                code: null,
                group_id: group.id,
                op_balance: openingBalance,
                op_balance_dc: balanceType,
                notes: "Auto-created for Opening Balance",
            },
        };

        try {
            const res = await axios.post(`${API_BASE_URL}/api/ledgers/add`, payload, { headers });
            if (res.data?.data?.Ledger) {
                setLedgers(prev => [...prev, { Ledger: res.data.data.Ledger, Group: group }]);
                return res.data.data.Ledger.id;
            }
            throw new Error("Failed to create ledger: No data returned");
        } catch (error) {
            console.error("Error creating ledger:", error);
            throw new Error(`Failed to create ledger "${ledgerName}": ${error.message}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            const validBanks = bankAccounts.filter(b => b.bank && b.amount);
            if (validBanks.length === 0) {
                throw new Error("At least one bank account with amount is required");
            }

            const headers = {
                "x-api-key": API_KEY,
                Accept: "application/json",
                "Content-Type": "application/json",
            };

            const totalRevenue = parseFloat(formData.total_revenue) || 0;
            const totalExpense = expenses.reduce((sum, exp) => {
                const parentAmt = parseFloat(exp.parentAmount) || 0;
                const ledgerAmt = (exp.ledgerExpenses || []).reduce(
                    (lSum, lExp) => lSum + (parseFloat(lExp.amount) || 0),
                    0
                );
                return sum + parentAmt + ledgerAmt;
            }, 0);
            const totalVatCollected = parseFloat(formData.vat_collected) || 0;
            const totalVatPayable = parseFloat(formData.vat_payable) || 0;

            const netProfit = totalRevenue - totalExpense - totalVatPayable + totalVatCollected;

            if (netProfit <= 0) {
                throw new Error("Net profit is zero or negative, nothing to allocate to banks");
            }

            const profitLedgerId = await ensureLedgerExists("Profit / Equity", "Capital Account", 0, "C");
            const totalBankAmount = validBanks.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);

            for (const bankAcc of validBanks) {
                const bankLedger = ledgers.find(l =>
                    l.Ledger?.name?.trim().toLowerCase() === bankAcc.bank?.trim().toLowerCase() &&
                    ["bank accounts", "cash-in-hand"].includes(l.Group?.name?.toLowerCase())
                )?.Ledger;

                if (!bankLedger) {
                    throw new Error(`Bank ledger "${bankAcc.bank}" not found`);
                }

                const bankRatio = parseFloat(bankAcc.amount) / totalBankAmount;
                const bankProfitAmount = netProfit * bankRatio;

                const receiptItems = [
                    { dc: "D", ledger_id: bankLedger.id, dr_amount: bankProfitAmount, cr_amount: null },
                    { dc: "C", ledger_id: profitLedgerId, dr_amount: null, cr_amount: bankProfitAmount },
                ];

                await axios.post(
                    `${API_BASE_URL}/api/entries/add/receipt`,
                    {
                        Entry: {
                            number: Date.now() + Math.random(),
                            date: `${formData.year}-03-31`,
                            ledger_id: bankLedger.id,
                            tag_id: null,
                            narration: `Net Profit Allocation for ${formData.year} - ${bankAcc.bank}`,
                            is_import: true,
                        },
                        Entryitem: receiptItems,
                    },
                    { headers }
                );
            }

            setMessage("✅ Net profit successfully allocated to banks!");
            onSuccess();
        } catch (error) {
            console.error("Upload error:", error);
            setMessage(`❌ Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Offcanvas show={show} placement="end" onHide={onClose} backdrop={true} style={{ width: '800px' }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Upload Financial Data</Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body>
                <div className="mb-3">
                    <label>Financial Year *</label>
                    <select
                        className="form-control"
                        name="year"
                        value={formData.year}
                        required
                        onChange={handleInputChange}
                    >
                        <option value="">Select</option>
                        <option value="2019">2019–20</option>
                        <option value="2020">2020–21</option>
                        <option value="2021">2021–22</option>
                        <option value="2022">2022–23</option>
                        <option value="2023">2023–24</option>
                    </select>
                </div>

                <div className="mb-3">
                    <label>Total Revenue *</label>
                    <input
                        type="number"
                        className="form-control"
                        name="total_revenue"
                        value={formData.total_revenue}
                        onChange={handleInputChange}
                    />
                </div>

                <h6 className="mt-3">Expenses</h6>

                {expenses.map((expense, expenseIndex) => (
                    <div key={expenseIndex} className="mb-4 p-3 border rounded">
                        <div className="row align-items-center mb-3">
                            <div className="col-10">
                                <label className="form-label">Select Expense Group</label>
                                <select
                                    className="form-control"
                                    value={expense.parentGroup}
                                    onChange={(e) => updateParentGroup(expenseIndex, e.target.value)}
                                >
                                    <option value="">Select Parent Group</option>
                                    {getParentExpenseGroups().map(group => (
                                        <option key={group.Group.id} value={group.Group.name}>
                                            {group.Group.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-2">
                                <button
                                    className="btn btn-light mt-4"
                                    onClick={() => removeExpenseRow(expenseIndex)}
                                >
                                    ✖
                                </button>
                            </div>
                        </div>

                        {expense.parentGroup && (
                            <div className="mb-3 p-2 border rounded bg-light">
                                <label className="form-label small">Total Amount for {expense.parentGroup}</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder={`Enter total amount`}
                                    value={expense.parentAmount}
                                    onChange={(e) => updateParentAmount(expenseIndex, e.target.value)}
                                />
                            </div>
                        )}

                        {expense.parentGroup && expense.ledgerExpenses.length > 0 && (
                            <div className="mt-3">
                                <label className="form-label small">Individual Ledger Amounts</label>
                                <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {expense.ledgerExpenses.map((ledgerExp) => (
                                        <div key={ledgerExp.ledgerId} className="row align-items-center mb-2">
                                            <div className="col-6">
                                                <span className="small">{ledgerExp.ledgerName}</span>
                                            </div>
                                            <div className="col-6">
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    placeholder="Amount"
                                                    value={ledgerExp.amount}
                                                    onChange={(e) => updateLedgerAmount(expenseIndex, ledgerExp.ledgerId, e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                <div className="mb-4">
                    <button className="btn btn-light w-100" onClick={addExpenseRow}>
                        + Add Expense Group
                    </button>
                </div>

                <div className="mb-3">
                    <label>VAT Payable</label>
                    <input
                        type="number"
                        className="form-control"
                        name="vat_payable"
                        value={formData.vat_payable}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="mb-3">
                    <label>VAT Collected</label>
                    <input
                        type="number"
                        className="form-control"
                        name="vat_collected"
                        value={formData.vat_collected}
                        onChange={handleInputChange}
                    />
                </div>

                <h6 className="mt-3">Bank Accounts</h6>
                {bankAccounts.map((bankAcc, i) => (
                    <div key={i} className="row mb-2 align-items-center">
                        <div className="col-8">
                            <select
                                className="form-control"
                                value={bankAcc.bank}
                                onChange={(e) => updateBankAccount(i, "bank", e.target.value)}
                            >
                                <option value="">Select Bank</option>
                                {banks?.map(bank => (
                                    <option key={bank.id} value={bank.name}>
                                        {bank.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-3">
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Amount"
                                value={bankAcc.amount}
                                onChange={(e) => updateBankAccount(i, "amount", e.target.value)}
                            />
                        </div>

                        <div className="col-1">
                            <button
                                className="btn btn-light"
                                onClick={() => setBankAccounts(bankAccounts.filter((_, idx) => idx !== i))}
                            >
                                ✖
                            </button>
                        </div>
                    </div>
                ))}

                <button className="btn btn-light mb-3" onClick={addBankRow}>
                    + Add Bank Account
                </button>

                <button className="btn btn-primary w-100" onClick={handleSubmit} disabled={loading}>
                    {loading ? "Uploading..." : "Save"}
                </button>

                {message && (
                    <p className="mt-3 text-center">{message}</p>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AccountUploadForm;